import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './Products.css';
import './AllSellStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import AddStockAdjustment from './AddStockAdjustment';
import StockAdjustmentReport from './StockAdjustmentReport';
import ViewStockAdjustmentModal from './ViewStockAdjustmentModal';
import apiService from '../../services/api';

const StockAdjustment = () => {
    const [activeTab, setActiveTab] = useState('list_stock_adjustment');
    const [adjustments, setAdjustments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedAdjustment, setSelectedAdjustment] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [perPage, setPerPage] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalAdjustments, setTotalAdjustments] = useState(0);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    
    // Filter State
    const [filters, setFilters] = useState({
        location_id: 'all',
        adjustment_type: 'all',
        start_date: '',
        end_date: ''
    });

    const [locations, setLocations] = useState([]);

    useEffect(() => {
        fetchLocations();
    }, []);

    const fetchLocations = async () => {
        try {
            const response = await apiService.getBusinessLocations();
            setLocations(response.data || []);
        } catch (error) {
            console.error('Failed to fetch locations', error);
        }
    };

    // Fetch data from API
    const fetchAdjustments = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = {
                page: currentPage,
                per_page: perPage,
                search: searchTerm
            };
            
            if (filters.location_id !== 'all') params.location_id = filters.location_id;
            if (filters.adjustment_type !== 'all') params.adjustment_type = filters.adjustment_type;
            if (filters.start_date) params.start_date = filters.start_date;
            if (filters.end_date) params.end_date = filters.end_date;

            const response = await apiService.getStockAdjustments(params);
            const data = response.data || response;
            setAdjustments(Array.isArray(data) ? data : (data.data || []));
            setTotalAdjustments(data.total || (Array.isArray(data) ? data.length : 0));
        } catch (err) {
            console.error('Failed to fetch adjustments', err);
            setError('Failed to load stock adjustments. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'list_stock_adjustment') {
            fetchAdjustments();
        }
    }, [activeTab, currentPage, perPage]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchAdjustments();
    };

    const handleApplyFilters = () => {
        setCurrentPage(1);
        fetchAdjustments();
        setShowFilters(false);
    };

    const handleResetFilters = () => {
        setFilters({
            location_id: 'all',
            adjustment_type: 'all',
            start_date: '',
            end_date: ''
        });
        setCurrentPage(1);
        // fetchAdjustments will be called by the effect if activeTab is list
        setShowFilters(false);
    };

    if (activeTab === 'add_stock_adjustment') {
        return <AddStockAdjustment onBack={() => setActiveTab('list_stock_adjustment')} onSaveSuccess={() => {
            setActiveTab('list_stock_adjustment');
            fetchAdjustments();
        }} />;
    }

    const handleExport = (format) => {
        const headers = ['Date', 'Reference No', 'Location', 'Adjustment type', 'Total Amount', 'Total amount recovered', 'Reason', 'Added By'];
        const rows = adjustments.map(adj => [
            adj.transaction_date || adj.date,
            adj.ref_no || adj.refNo,
            adj.business_location?.name || adj.location || 'Main Location',
            adj.adjustment_type || adj.type,
            (parseFloat(adj.final_total || adj.totalAmount || 0)).toFixed(2),
            (parseFloat(adj.amount_recovered || adj.amountRecovered || 0)).toFixed(2),
            adj.additional_notes || adj.reason || '',
            adj.created_by_name || adj.addedBy || 'N/A'
        ]);

        const title = 'Stock Adjustment List';
        const filename = 'stock_adjustments';

        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();

        setShowExportDropdown(false);
    };

    const handleAddAdjustment = () => {
        setActiveTab('add_stock_adjustment');
    };

    const handleViewDetails = (adjustment) => {
        setSelectedAdjustment(adjustment);
        setShowViewModal(true);
    };

    return (
        <div className="products-container">
            <div className="main-card">
                <div className="product-tabs">
                    <div 
                        className={`product-tab ${activeTab === 'list_stock_adjustment' ? 'active' : ''}`}
                        onClick={() => setActiveTab('list_stock_adjustment')}
                    >
                        <i className="fa fa-list"></i> List Stock Adjustment
                    </div>
                </div>

                <div className="table-controls">
                    <div className="table-controls-left">
                        <div className="entries-dropdown">
                            <span>Show</span>
                            <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>

                    <div className="table-controls-right">
                        <div className="tabs-search-wrapper">
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="tabs-search-input"
                            />
                            <button className="filter-toggle-btn" onClick={() => setShowFilters(true)} title="Open Filters">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                                </svg>
                                Filters
                            </button>
                        </div>
                        <div className="export-dropdown-container" style={{ position: 'relative' }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu">
                                    <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                                    <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                                    <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                                    <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                                </div>
                            )}
                        </div>
                        <button className="control-btn-primary" onClick={handleAddAdjustment}>
                            <span className="plus-icon">+</span> Add
                        </button>
                    </div>
                </div>

                <div className="table-content-card">
                    <div className="products-table-container">
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>Action</th>
                                    <th>Date</th>
                                    <th>Reference No</th>
                                    <th>Location</th>
                                    <th>Adjustment type</th>
                                    <th>Total Amount</th>
                                    <th>Total amount recovered</th>
                                    <th>Reason</th>
                                    <th>Added By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>Fetching stock adjustments...</td></tr>
                                ) : adjustments.length === 0 ? (
                                    <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>No stock adjustments found.</td></tr>
                                ) : (
                                    adjustments.map((adj) => (
                                        <tr key={adj.id}>
                                            <td>
                                                <button 
                                                    className="action-btn-view" 
                                                    onClick={() => handleViewDetails(adj)}
                                                    style={{ padding: '0.4rem 1rem', background: '#fff', border: '1px solid #8b5cf6', color: '#8b5cf6', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer' }}
                                                >
                                                    View
                                                </button>
                                            </td>
                                            <td>{adj.transaction_date || adj.date}</td>
                                            <td style={{ fontWeight: '700' }}>{adj.ref_no || adj.refNo}</td>
                                            <td>{adj.business_location?.name || adj.location || 'Main Location'}</td>
                                            <td>
                                                <span style={{ 
                                                    padding: '0.25rem 0.75rem', 
                                                    borderRadius: '9999px', 
                                                    fontSize: '0.7rem', 
                                                    fontWeight: '800',
                                                    background: (adj.adjustment_type || adj.type)?.toLowerCase() === 'abnormal' ? '#fee2e2' : '#f0fdf4',
                                                    color: (adj.adjustment_type || adj.type)?.toLowerCase() === 'abnormal' ? '#b91c1c' : '#15803d',
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {adj.adjustment_type || adj.type}
                                                </span>
                                            </td>
                                            <td style={{ fontWeight: '700' }}>${(parseFloat(adj.final_total || adj.totalAmount || 0)).toFixed(2)}</td>
                                            <td>${(parseFloat(adj.amount_recovered || adj.amountRecovered || 0)).toFixed(2)}</td>
                                            <td style={{ fontStyle: 'italic' }}>{adj.additional_notes || adj.reason || '--'}</td>
                                            <td>{adj.created_by_name || adj.addedBy || 'N/A'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                            Showing {adjustments.length > 0 ? ((currentPage - 1) * perPage) + 1 : 0} to {Math.min(currentPage * perPage, totalAdjustments)} of {totalAdjustments} entries
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button 
                                className="pagination-btn" 
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            >Previous</button>
                            <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>Page {currentPage}</span>
                            <button 
                                className="pagination-btn" 
                                disabled={currentPage * perPage >= totalAdjustments}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                            >Next</button>
                        </div>
                    </div>
                </div>
            </div>
            
            {showViewModal && <ViewStockAdjustmentModal adjustment={selectedAdjustment} onClose={() => setShowViewModal(false)} />}

            {showFilters && (
                <div className="modal-overlay" onClick={() => setShowFilters(false)}>
                    <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
                        <div className="modal-header">
                            <h2><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
                            <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
                        </div>
                        <div className="modal-content" style={{ padding: '1.5rem' }}>
                            <div className="filter-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div className="filter-group">
                                    <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Location:</label>
                                    <select 
                                        style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none' }}
                                        value={filters.location_id}
                                        onChange={(e) => setFilters({...filters, location_id: e.target.value})}
                                    >
                                        <option value="all">All Locations</option>
                                        {locations.map(loc => (
                                            <option key={loc.id} value={loc.id}>{loc.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Adjustment Type:</label>
                                    <select 
                                        style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none' }}
                                        value={filters.adjustment_type}
                                        onChange={(e) => setFilters({...filters, adjustment_type: e.target.value})}
                                    >
                                        <option value="all">All Types</option>
                                        <option value="normal">Normal</option>
                                        <option value="abnormal">Abnormal</option>
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>Start Date:</label>
                                    <input 
                                        type="date" 
                                        style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none' }}
                                        value={filters.start_date}
                                        onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                                    />
                                </div>
                                <div className="filter-group">
                                    <label style={{ fontWeight: '600', marginBottom: '0.4rem', display: 'block', fontSize: '0.875rem' }}>End Date:</label>
                                    <input 
                                        type="date" 
                                        style={{ width: '100%', padding: '0.6rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem', outline: 'none' }}
                                        value={filters.end_date}
                                        onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0' }}>
                            <button 
                                className="modal-btn" 
                                onClick={handleResetFilters}
                                style={{ padding: '0.6rem 1.25rem', border: '1px solid #e2e8f0', background: '#fff', borderRadius: '8px', cursor: 'pointer' }}
                            >Reset</button>
                            <button 
                                className="modal-btn primary-btn" 
                                onClick={handleApplyFilters}
                                style={{ padding: '0.6rem 1.25rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                            >Apply Filters</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockAdjustment;
